import { nextTick, watch } from 'vue';
import { storeToRefs } from 'pinia';
import { useHFetch } from './http/use-http-request';
import { refreshAppSettings } from './use-update-settings';
import { useOUserSettingsStore } from '@/stores/use-user-settings-store';

let resolveAuthenticatedSettingsLoad: (() => void) | null = null;
let authenticatedSettingsLoadResolved = false;

/**
 * Resolves once we know whether the user is authenticated and have attempted
 * to load app config settings with that auth context (required for maintenance mode).
 */
export const authenticatedSettingsLoadPromise = new Promise<void>((resolve) => {
  resolveAuthenticatedSettingsLoad = resolve;
});

function markAuthenticatedSettingsLoaded() {
  if (authenticatedSettingsLoadResolved) {
    return;
  }

  authenticatedSettingsLoadResolved = true;
  resolveAuthenticatedSettingsLoad?.();
  resolveAuthenticatedSettingsLoad = null;
}

/**
 * Loads app config settings after /my/settings completes.
 * The initial bootstrap in hInstallHelixCore often runs before auth cookies exist.
 */
export function useSettingsLoadWhenAuthenticated(settingsUrl = 'my/config/settings') {
  const httpClient = useHFetch();
  const userSettingsStore = useOUserSettingsStore();
  const { oUserSettings, oUserSettingsLoading } = storeToRefs(userSettingsStore);

  let lastRefreshedUserKey: string | null = null;

  watch(
    [() => oUserSettings.value?.key, oUserSettingsLoading],
    async ([userKey, userLoading]) => {
      if (userLoading) {
        return;
      }

      if (!userKey) {
        markAuthenticatedSettingsLoaded();
        return;
      }

      if (lastRefreshedUserKey === userKey) {
        return;
      }

      lastRefreshedUserKey = userKey;
      await refreshAppSettings(settingsUrl, httpClient).catch(() => {});
      // Wait for Vue to flush the watchApiSettings watcher so defaultSettings
      // reflects the fetched apiSettings before guards and redirects evaluate it.
      await nextTick();
      markAuthenticatedSettingsLoaded();
    },
    { immediate: true },
  );
}
